import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import { Button2 } from "@/ui2/atoms/Button";
import type { CancelledActivity, UUID } from "@/core/types";
import dayjs from "@/lib/helpers/dayjs";
import { getUsernameFromToken } from "@/utils/authToken";
import { Heading2 } from "@/ui2/atoms/Heading";
import { ModalControlled2, ModalDivider } from "@/ui2/molecules/Modal";
import {
  selectIsHCPPendingCancellationAcknowledgement,
  selectIsPatientInactive,
} from "@/redux/selectors/authSelectors";
import { useAppendAcknowledgement } from "@/features/consent/api/mutations/useAppendAcknowledgement";

import { activityListKeys } from "../api/keyFactory";
import { useActivityList } from "../api/queries/useActivityList";

export function HCPAutoCancelModal() {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(true);

  const isHCPPendingCancellationAcknowledgement = useSelector(
    selectIsHCPPendingCancellationAcknowledgement,
  );
  const isInactivePatient = useSelector(selectIsPatientInactive);

  const { data: cancelledSessions } = useActivityList({
    isEnabled: isHCPPendingCancellationAcknowledgement,
    type: "cancelled",
  });

  const autoCancelledSession =
    cancelledSessions?.find(
      (session): session is CancelledActivity =>
        session.type === "cancelled" && session.key_content.is_auto_cancelled,
    ) ?? null;

  const { mutate: appendAcknowledgement } = useAppendAcknowledgement({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activityListKeys.byType("cancelled") });
    },
  });

  if (isInactivePatient || !autoCancelledSession) return null;

  const onAcknowledge = () => {
    appendAcknowledgement({
      acknowledgement: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        acknowledged_by: getUsernameFromToken()!,
        activity_id: autoCancelledSession.entity_id as UUID,
        client: "web",
        date: dayjs.utc().toISOString(),
        timezone: dayjs.tz.guess(),
      },
      acknowledgement_name: "hcp_auto_cancellation",
    });
  };

  return (
    <ModalControlled2 className="tw:max-w-133" isOpen={isOpen} onOpenChange={setIsOpen}>
      <Heading2 size={3}>Welcome to aptihealth!</Heading2>
      <ModalDivider />

      <p className="body-md">
        We're happy to see you and congratulations on taking the next step to feeling better. We
        were unable to hold your appointment time because your registration wasn't complete.
      </p>

      <p className="body-md">Not to worry, let's find a new time.</p>

      <Button2
        className="tw:min-w-40 tw:self-center"
        onPress={() => {
          onAcknowledge();
          history.push("/app/schedule/assign?mode=new");
        }}
      >
        Schedule
      </Button2>
    </ModalControlled2>
  );
}
